import type { TranslationMap } from "../lib/types";

export const en: TranslationMap = {
  common: {
    version: "Version",
    health: "Health",
    ok: "OK",
    offline: "Offline",
    connect: "Connect",
    refresh: "Refresh",
    enabled: "Enabled",
    disabled: "Disabled",
    na: "n/a",
    docs: "Docs",
    resources: "Resources",
  },
  nav: {
    chat: "Chat",
    control: "Control",
    agent: "Agent",
    settings: "Settings",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
  },
  tabs: {
    agents: "Agents",
    overview: "Overview",
    channels: "Channels",
    instances: "Instances",
    sessions: "Sessions",
    usage: "Usage",
    cron: "Cron Jobs",
    llm: "LLM Models",
    account: "Account",
    skills: "Skills",
    nodes: "Nodes",
    chat: "Chat",
    config: "Config",
    debug: "Debug",
    logs: "Logs",
  },
  subtitles: {
    agents: "Manage agent workspaces, tools, and identities.",
    overview: "Gateway status, entry points, and a fast health read.",
    channels: "Manage channels and settings.",
    instances: "Presence beacons from connected clients and nodes.",
    sessions: "Inspect active sessions and adjust per-session defaults.",
    usage: "Monitor API usage and costs.",
    cron: "Schedule wakeups and recurring agent runs.",
    llm: "Manage AI models and API keys.",
    account: "User profile and billing.",
    skills: "Manage skill availability and API key injection.",
    nodes: "Paired devices, capabilities, and command exposure.",
    chat: "Direct gateway chat session for quick interventions.",
    config: "Edit ~/.openclaw/openclaw.json safely.",
    debug: "Gateway snapshots, events, and manual RPC calls.",
    logs: "Live tail of the gateway file logs.",
  },
  overview: {
    access: {
      title: "Gateway Access",
      subtitle: "Where the dashboard connects and how it authenticates.",
      wsUrl: "WebSocket URL",
      token: "Gateway Token",
      password: "Password (not stored)",
      sessionKey: "Default Session Key",
      language: "Language",
      connectHint: "Click Connect to apply connection changes.",
      trustedProxy: "Authenticated via trusted proxy.",
    },
    snapshot: {
      title: "Snapshot",
      subtitle: "Latest gateway handshake information.",
      status: "Status",
      uptime: "Uptime",
      tickInterval: "Tick Interval",
      lastChannelsRefresh: "Last Channels Refresh",
      channelsHint: "Use Channels to link WhatsApp, Telegram, Discord, Signal, or iMessage.",
    },
    stats: {
      instances: "Instances",
      instancesHint: "Presence beacons in the last 5 minutes.",
      sessions: "Sessions",
      sessionsHint: "Recent session keys tracked by the gateway.",
      cron: "Cron",
      cronNext: "Next wake {time}",
    },
    notes: {
      title: "Notes",
      subtitle: "Quick reminders for remote control setups.",
      tailscaleTitle: "Tailscale serve",
      tailscaleText: "Prefer serve mode to keep the gateway on loopback with tailnet auth.",
      sessionTitle: "Session hygiene",
      sessionText: "Use /new or sessions.patch to reset context.",
      cronTitle: "Cron reminders",
      cronText: "Use isolated sessions for recurring runs.",
    },
    auth: {
      required: "This gateway requires auth. Add a token or password, then click Connect.",
      failed:
        "Auth failed. Re-copy a tokenized URL with {command}, or update the token, then click Connect.",
    },
    pairing: {
      hint: "This device needs pairing approval from the gateway host.",
      mobileHint:
        "On mobile? Copy the full URL (including #token=...) from openclaw dashboard --no-open on your desktop.",
    },
    insecure: {
      hint: "This page is HTTP, so the browser blocks device identity. Use HTTPS (Tailscale Serve) or open {url} on the gateway host.",
      stayHttp: "If you must stay on HTTP, set {config} (token-only).",
    },
  },
  login: {
    title: "OpenHub",
    subtitle: "Open social media platform service",
    passwordLogin: "Password Login",
    phoneLogin: "Phone Login",
    wechatLogin: "WeChat Login",
    email: "Email",
    password: "Password",
    register: "Register",
    login: "Login",
    hasAccount: "Already have an account? Login",
    noAccount: "No account? Register",
    phone: "Phone number",
    sendCode: "Send Code",
    verificationCode: "Verification code",
    scanQrCode: "Please scan with WeChat to login",
    loading: "Loading...",
    testEnvCode: "Test environment verification code:",
    errors: {
      operationFailed: "Operation failed",
      invalidToken: "Invalid token received",
      sendCodeFailed: "Failed to send verification code",
      codeError: "Verification code error or expired",
      loadQrFailed: "Failed to load QR code",
    },
  },
  channels: {
    loadFailed: "Failed to load channels",
    fillRequired: "Please fill in required fields",
    updateSuccess: "Channel updated successfully",
    createSuccess: "Channel created successfully",
    saveFailed: "Failed to save",
    cannotDeleteSystem: "System channels cannot be deleted",
    confirmDelete: "Are you sure you want to delete channel \"{name}\"?",
    deleteSuccess: "Channel deleted successfully",
    deleteFailed: "Failed to delete",
    enable: "Enable",
    disable: "Disable",
    statusChanged: "Channel {status}",
    toggleStatusFailed: "Failed to toggle status",
    testSuccessWithLatency: "Test successful ({latency}ms)",
    testSuccess: "Test successful",
    testFailed: "Test failed: {message}",
    testFailedGeneric: "Test failed",
    healthCheckComplete: "Health check complete",
    healthCheckFailed: "Health check failed",
    unshareSuccess: "Share cancelled",
    unshareFailed: "Failed to cancel share",
    fillValidPriceCapacity: "Please fill in valid price and capacity",
    shareToPoolSuccess: "Channel shared to compute pool",
    shareFailed: "Failed to share",
    allChannelsAlready: "All channels already {action}",
    enableSmartRoutingConfirm: "After enabling smart routing, the system will automatically select the optimal channel to process requests.",
    systemChannelsCostWarning: "Note: Using system channels will incur charges, billed based on actual usage.",
    confirmEnableChannels: "Are you sure you want to enable {count} system channels?",
    bulkToggleSuccess: "{count} channels {action}",
    bulkToggleFailed: "Failed to bulk {action}",
    editChannel: "Edit Channel",
    addChannel: "Add Channel",
    channelName: "Channel Name",
    namePlaceholder: "e.g. OpenAI Main Channel",
    provider: "Provider",
    apiEndpoint: "API Endpoint",
    baseUrlPlaceholder: "Leave empty to use default",
    apiKey: "API Key",
    leaveUnchanged: "(Leave unchanged)",
    apiKeyPlaceholder: "sk-...",
    priority: "Priority",
    priorityHint: "Smaller values mean higher priority",
    cancel: "Cancel",
    saving: "Saving...",
    save: "Save",
    loading: "Loading...",
    systemChannels: "System Channels",
    systemChannelsSub: "Admin-configured global shared channels (read-only)",
    smartRouting: "Smart Routing",
    toggleSmartRouting: "Enable/Disable Smart Routing",
    myApiKeys: "My API Keys",
    myApiKeysSub: "Private channels you configured yourself",
    noPrivateChannels: "No private channels yet",
    addFirstKeyHint: "Add your first API Key to get started",
    addApiKey: "Add API Key",
    usageGuide: "Usage Guide",
    usageGuideSub: "How to configure API channels",
    guide1Title: "Select Provider",
    guide1Content: "Choose from the list of supported providers or use a custom endpoint",
    guide2Title: "Configure API Key",
    guide2Content: "Enter the API Key for the corresponding platform, multiple AI providers supported",
    guide3Title: "Set Priority",
    guide3Content: "Smaller values mean higher priority, can be used for load balancing and failover",
    system: "System",
    clickToTest: "Click to test connection",
    testing: "Testing...",
    healthy: "Available",
    unhealthy: "Unavailable",
    unknown: "Unknown",
    endpoint: "Endpoint",
    default: "Default",
    status: "Status",
    enabled: "Enabled",
    disabled: "Disabled",
    models: "Models",
    shareToPool: "Share to Compute Pool",
    shared: "Shared",
    cancelShare: "Cancel Share",
    enableShare: "Enable Share",
    pricePerMillionTokens: "Price (per million tokens)",
    pricePlaceholder: "e.g. 2.50",
    dailyCapacity: "Daily Capacity (tokens)",
    capacityPlaceholder: "e.g. 1000000",
    submitting: "Submitting...",
    confirmShare: "Confirm Share",
    price: "Price",
    perMillionTokens: "million tokens",
    disableThisChannel: "Disable this channel",
    enableThisChannel: "Enable this channel",
    edit: "Edit",
    delete: "Delete",
  },
  keys: {
    loadFailed: "Failed to load keys",
    createSuccess: "Key created successfully",
    createFailed: "Failed to create key",
    updateSuccess: "Key updated successfully",
    updateFailed: "Failed to update key",
    deleteSuccess: "Key deleted successfully",
    deleteFailed: "Failed to delete key",
    confirmDelete: "Are you sure you want to delete this key?",
    copied: "Copied to clipboard",
    copyFailed: "Failed to copy",
    loading: "Loading...",
    noApiKeys: "No API keys yet",
    createFirstKeyHint: "Create your first API key to get started",
    createKey: "Create Key",
    name: "Name",
    key: "Key",
    createdAt: "Created At",
    lastUsedAt: "Last Used",
    actions: "Actions",
    edit: "Edit",
    delete: "Delete",
    viewSubscription: "View Subscription",
    codingPlanKey: "Coding Plan Key",
    codingPlanKeyDesc1: "A unique API key associated with each person. Please fill in this key in your browser plugin or other client code.",
    codingPlanKeyDesc2: "To protect your account security, MiniMax may automatically verify any API keys we find have been publicly leaked.",
    view: "View",
    copy: "Copy",
    createNewKey: "Create New Key",
    namePlaceholder: "Please enter key name",
    save: "Save",
    cancel: "Cancel",
    editKey: "Edit Key",
    createSuccessTitle: "Key Created Successfully",
    saveKeyHint: "Please save your API key. This key will only be displayed once.",
    copyKey: "Copy Key",
    close: "Close",
  },
  billing: {
    loadFailed: "Failed to load billing data",
    rechargeFailed: "Recharge failed",
    rechargeFailedAlert: "Recharge failed, please try again",
    loading: "Loading...",
    accountBalance: "Account Balance",
    recharge: "Recharge",
    todayCost: "Today's Cost",
    monthCost: "This Month",
    totalCost: "Total Cost",
    transactionHistory: "Transaction History",
    noTransactions: "No transaction records yet",
    accountRecharge: "Account Recharge",
    rechargeAmount: "Recharge Amount",
    rechargeAmountPlaceholder: "Please enter recharge amount",
    minRechargeAmount: "Minimum recharge amount: ¥1.00",
    rechargeInstant: "Recharge arrives immediately",
    cancel: "Cancel",
    confirmRecharge: "Confirm Recharge",
  },
  billingHistory: {
    loadFailed: "Failed to load billing records",
    exportDeveloping: "Export feature is being developed",
    filterTitle: "Filter",
    consumerAccount: "Consumer Account",
    consumerEndpoint: "Consumer Endpoint",
    startDate: "Start Date",
    endDate: "End Date",
    selectAccountPlaceholder: "Select account",
    all: "All",
    export: "Export",
    currentFilterCount: "Current filter count: {count} records",
    loading: "Loading...",
    noRecords: "No billing records",
    requestId: "Request ID",
    apiKeyName: "API Key Name",
    endpoint: "Endpoint",
    model: "Model",
    consumedAmount: "Consumed",
    consumedAfterCoupon: "After Coupon",
    inputTokens: "Input Tokens",
    outputTokens: "Output Tokens",
    totalTokens: "Total Tokens",
    requestTime: "Time",
    status: "Status",
    mainAccount: "Main Account",
    previousPage: "Previous",
    pageOf: "Page {page} of {total}, {count} total",
    nextPage: "Next",
  },
  stats: {
    loadFailed: "Failed to load stats",
    retry: "Retry",
    filterTitle: "Filter",
    loading: "Loading usage data...",
    loadingData: "Loading",
    pinned: "Pinned",
    pin: "Pin",
    unpinFilter: "Unpin filter",
    today: "Today",
    sevenDays: "7 Days",
    thirtyDays: "30 Days",
    to: "to",
    tokens: "Tokens",
    cost: "Cost",
    refresh: "Refresh",
    consumption: "Consumption",
    usageOverview: "Usage Overview",
    totalTokens: "Total Tokens",
    totalCost: "Total Cost",
    inputTokens: "Input Tokens",
    outputTokens: "Output Tokens",
    cacheRead: "Cache Read",
    cacheHitRate: "Cache Hit Rate",
    inputOutputCache: "Input + Output + Cache",
    cumulativeSpend: "Cumulative spend",
    cacheEfficiency: "Cache efficiency",
    dailyUsage: "Daily Usage",
    noData: "No data",
    total: "Total",
    byType: "By Type",
    dailyTokenUsage: "Daily {type} Usage",
    modelUsage: "Model Usage",
    model: "Model",
    provider: "Provider",
    requestCount: "Requests",
    tokensCol: "Tokens",
    costCol: "Cost",
    unknown: "Unknown",
  },
  dashboard: {
    loadUserFailed: "Failed to load user info",
    loadDataFailed: "Failed to load data",
    networkError: "Network error",
    loadStatsFailed: "Failed to load detailed stats",
    loadingStats: "Loading statistics...",
    loading: "Loading...",
    retry: "Retry",
    today: "Today",
    sevenDays: "7 Days",
    thirtyDays: "30 Days",
    to: "to",
    tokens: "Tokens",
    cost: "Cost",
    totalTokens: "Total Tokens",
    totalCost: "Total Cost",
    inputOutputCache: "Input + Output + Cache",
    cumulativeSpend: "Cumulative spend",
    inputTokens: "Input Tokens",
    outputTokens: "Output Tokens",
    cacheRead: "Cache Read",
    cacheHitRate: "Cache Hit Rate",
    cacheEfficiency: "Cache efficiency",
    dailyUsage: "Daily {type} Usage",
    noData: "No data",
    modelUsage: "Model Usage",
    model: "Model",
    provider: "Provider",
    requestCount: "Requests",
    tokensCol: "Tokens",
    costCol: "Cost",
    unknown: "Unknown",
    accountBalance: "Account Balance",
    availableBalance: "Available balance",
    todayCost: "Today's Cost",
    todayRequests: "{count} requests",
    monthCost: "This Month's Cost",
    monthRequests: "{count} requests",
    usageStats: "Usage Statistics",
    viewUsageDetails: "View detailed usage data and trends",
    expandStats: "Expand Stats",
    collapseStats: "Collapse Stats",
    gettingStarted: "Getting Started",
    completeSteps: "Complete the following steps to start using OpenHub ({count}/4 completed)",
    selectMode: "Select Usage Mode",
    alreadySelected: "Selected: {mode}",
    switchModeInSettings: "You can switch modes in settings",
    chooseSelfManaged: "Self-managed (use your API Keys) or Managed (use platform service)",
    selectNow: "Select Now",
    configureLLM: "Configure LLM Service",
    addApiKeys: "Add your OpenAI, Claude, and other API Keys",
    rechargeBalance: "Recharge balance to use platform service",
    configureNow: "Configure Now",
    createApiKey: "Create API Key",
    generateKey: "Generate a key for calling OpenHub API",
    createNow: "Create Now",
    startUsing: "Start Using",
    useOpenAICompatible: "Use OpenAI-compatible API to call LLM services",
    viewDocs: "View Docs",
    switchModeFailed: "Failed to switch mode",
    selfManagedMode: "Self-managed Mode",
    managedMode: "Managed Mode",
  },
  models: {
    settingsSaved: "Settings saved",
    saveSettingsFailed: "Failed to save settings",
    saveFailed: "Save failed",
    loadDataFailed: "Failed to load data",
    resetToDefault: "Reset to default",
    searchModels: "Search models...",
    modelConfigOverview: "Model Configuration Overview",
    modelName: "Model Name",
    provider: "Provider",
    status: "Status",
    enabled: "Enabled",
    available: "Available",
    unavailable: "Unavailable",
    yes: "Yes",
    no: "No",
    enableThisModel: "Enable this model",
    resetDefault: "Reset Default",
    saving: "Saving...",
    saveSettings: "Save Settings",
    adjustParameters: "Adjust parameters for {model}",
    temperature: "Temperature (Creativity)",
    temperatureHint: "Lower values are more deterministic, higher values are more creative",
    maxTokens: "Max Tokens (Max Output)",
    topP: "Top P (Sampling)",
    topPHint: "Controls diversity of sampling",
    frequencyPenalty: "Frequency Penalty",
    frequencyPenaltyHint: "Reduces repetition likelihood",
    presencePenalty: "Presence Penalty",
    presencePenaltyHint: "Encourages talking about new topics",
    inputPrice: "Input Price",
    outputPrice: "Output Price",
    modelInputPrice: "Price for model processing input text",
    modelOutputPrice: "Price for model generating output text",
    billingUnit: "Billing Unit",
    billingUnitHint: "Prices are per million tokens",
    costEstimate: "Cost Estimate",
    costEstimateHint: "Actual cost calculated based on usage",
    usageStatistics: "Usage Statistics",
    llmApiUsage: "LLM API Usage Statistics",
    totalCalls: "Total Calls",
    totalTokens: "Total Tokens",
    totalCost: "Total Cost",
    statsPeriod: "Stats Period",
    noUsageData: "No usage data",
    selectModel: "Select a Model",
    selectModelHint: "Select a model from the left list to view and configure its settings",
  },
  groups: {
    loadGroupsFailed: "Failed to load groups",
    createGroupFailed: "Failed to create group",
    deleteGroupFailed: "Failed to delete group",
    loadMembersFailed: "Failed to load members",
    removeMemberFailed: "Failed to remove member",
    addMemberFailed: "Failed to add member",
    confirmDeleteGroup: "Are you sure you want to delete this group?",
    confirmRemoveMember: "Are you sure you want to remove this member?",
    enterUserId: "Please enter user ID",
    owner: "Owner",
    admin: "Admin",
    member: "Member",
    loading: "Loading...",
    noGroups: "No user groups",
    noGroupsHint: "Create your first user group to start managing",
    createGroup: "Create Group",
    groupName: "Group Name",
    description: "Description",
    status: "Status",
    memberCount: "Members",
    createdAt: "Created At",
    actions: "Actions",
    active: "Active",
    inactive: "Inactive",
    members: "Members",
    delete: "Delete",
    createUserGroup: "Create User Group",
    cancel: "Cancel",
    create: "Create",
    manageGroupMembers: "Manage Group Members - {name}",
    addMember: "Add Member",
    userId: "User ID",
    enterUserIdPlaceholder: "Enter user ID",
    role: "Role",
    memberList: "Member List ({count})",
    noMembers: "No members",
    user: "User",
    email: "Email",
    joinedAt: "Joined At",
    remove: "Remove",
    add: "Add",
  },
  adminPricing: {
    loadPricingFailed: "Failed to load pricing data",
    pricingUpdated: "Pricing updated",
    updatePricingFailed: "Failed to update pricing",
    lossRateOutOfRange: "Loss Rate must be between 5% - 30%",
    lossRateUpdated: "Loss rate updated",
    updateLossRateFailed: "Failed to update loss rate",
    pricingCreated: "Pricing created",
    createPricingFailed: "Failed to create pricing",
    loading: "Loading...",
    addModelPricing: "Add Model Pricing",
    noPricingData: "No pricing data",
    input: "Input",
    output: "Output",
    minListing: "Min Listing",
    avgListing: "Avg Listing",
    lossRate: "Loss Rate",
    estimatedMargin: "Est. Margin",
    edit: "Edit",
    editPricingFor: "Edit Pricing - {model}",
    inputPricePerMillion: "Input Price (per million tokens)",
    outputPricePerMillion: "Output Price (per million tokens)",
    saving: "Saving...",
    savePricing: "Save Pricing",
    lossRateAdjustment: "Loss Rate Adjustment",
    saveLossRate: "Save Loss Rate",
    cancel: "Cancel",
    modelName: "Model Name",
    create: "Create",
    creating: "Creating...",
  },
  adminComputePool: {
    loadFailed: "Failed to load",
    weightSumMustBeOne: "Weights must sum to 1.0, current sum is {sum}",
    weightsUpdated: "Weights updated",
    updateFailed: "Update failed",
    platformResourceCreated: "Platform resource created",
    createFailed: "Create failed",
    bidWeightsConfig: "Bid Weights Configuration",
    priceWeight: "Price Weight",
    qualityWeight: "Quality Weight",
    latencyWeight: "Latency Weight",
    saving: "Saving...",
    saveWeights: "Save Weights",
    total: "Total",
    modelName: "Model Name",
    minPrice: "Min Price",
    maxPrice: "Max Price",
    allTypes: "All Types",
    platformResource: "Platform Resource",
    userShared: "User Shared",
    allStatus: "All Status",
    active: "Active",
    inactive: "Inactive",
    suspended: "Suspended",
    filter: "Filter",
    createPlatformResource: "Create Platform Resource",
    loading: "Loading...",
    noMatchingListings: "No matching listings",
    dailyCapacity: "Daily Capacity",
    quality: "Quality",
    createPlatformBuiltInResource: "Create Platform Built-in Resource",
    apiKey: "API Key",
    baseUrl: "Base URL",
    pricingPerMillion: "Price (per million tokens)",
    cancel: "Cancel",
    create: "Create",
    creating: "Creating...",
    listingActive: "Active",
    listingSuspended: "Paused",
    listingInactive: "Offline",
  },
  pricing: {
    monthlyBilling: "Monthly Subscription",
    yearlyBilling: "Yearly Subscription",
    saveTwoMonths: "Save 2 months",
    mostPopular: "Most Popular",
    processing: "Processing...",
    startFree: "Start Free",
    subscribeNow: "Subscribe Now",
    subscribeSuccess: "Subscribed to {name} ({cycle}) successfully!",
    subscribeError: "Subscription failed",
    subscribeFailed: "Subscription failed, please try again",
    subscriptionNotes: "Subscription Notes",
    note1: "All prices are in CNY, including taxes",
    note2: "Billed monthly, auto-renews, cancel anytime",
    note3: "Overage billed at ¥0.01 per call",
    note4: "Enterprise yearly plan gets 15% off",
    note5: "New users get 50% off first month",
  },
  chat: {
    disconnected: "Disconnected from gateway.",
    refreshTitle: "Refresh chat data",
    thinkingToggle: "Toggle assistant thinking/working output",
    focusToggle: "Toggle focus mode (hide sidebar + page header)",
    hideCronSessions: "Hide cron sessions",
    showCronSessions: "Show cron sessions",
    showCronSessionsHidden: "Show cron sessions ({count} hidden)",
    onboardingDisabled: "Disabled during onboarding",
  },
  languages: {
    en: "English",
    zhCN: "简体中文 (Simplified Chinese)",
    zhTW: "繁體中文 (Traditional Chinese)",
    ptBR: "Português (Brazilian Portuguese)",
    de: "Deutsch (German)",
  },
  cron: {
    summary: {
      enabled: "Enabled",
      yes: "Yes",
      no: "No",
      jobs: "Jobs",
      nextWake: "Next wake",
      refreshing: "Refreshing...",
      refresh: "Refresh",
    },
    jobs: {
      title: "Jobs",
      subtitle: "All scheduled jobs stored in the gateway.",
      shownOf: "{shown} shown of {total}",
      searchJobs: "Search jobs",
      searchPlaceholder: "Name, description, or agent",
      enabled: "Enabled",
      schedule: "Schedule",
      lastRun: "Last run",
      all: "All",
      sort: "Sort",
      nextRun: "Next run",
      recentlyUpdated: "Recently updated",
      name: "Name",
      direction: "Direction",
      ascending: "Ascending",
      descending: "Descending",
      reset: "Reset",
      noMatching: "No matching jobs.",
      loading: "Loading...",
      loadMore: "Load more jobs",
    },
    runs: {
      title: "Run history",
      subtitleAll: "Latest runs across all jobs.",
      subtitleJob: "Latest runs for {title}.",
      scope: "Scope",
      allJobs: "All jobs",
      selectedJob: "Selected job",
      searchRuns: "Search runs",
      searchPlaceholder: "Summary, error, or job",
      newestFirst: "Newest first",
      oldestFirst: "Oldest first",
      status: "Status",
      delivery: "Delivery",
      clear: "Clear",
      allStatuses: "All statuses",
      allDelivery: "All delivery",
      selectJobHint: "Select a job to inspect run history.",
      noMatching: "No matching runs.",
      loadMore: "Load more runs",
      runStatusOk: "OK",
      runStatusError: "Error",
      runStatusSkipped: "Skipped",
      runStatusUnknown: "Unknown",
      deliveryDelivered: "Delivered",
      deliveryNotDelivered: "Not delivered",
      deliveryUnknown: "Unknown",
      deliveryNotRequested: "Not requested",
    },
    form: {
      editJob: "Edit Job",
      newJob: "New Job",
      updateSubtitle: "Update the selected scheduled job.",
      createSubtitle: "Create a scheduled wakeup or agent run.",
      required: "Required",
      requiredSr: "required",
      basics: "Basics",
      basicsSub: "Name it, choose the assistant, and set enabled state.",
      fieldName: "Name",
      description: "Description",
      agentId: "Agent ID",
      namePlaceholder: "Morning brief",
      descriptionPlaceholder: "Optional context for this job",
      agentPlaceholder: "main or ops",
      agentHelp: "Start typing to pick a known agent, or enter a custom one.",
      schedule: "Schedule",
      scheduleSub: "Control when this job runs.",
      every: "Every",
      at: "At",
      cronOption: "Cron",
      runAt: "Run at",
      unit: "Unit",
      minutes: "Minutes",
      hours: "Hours",
      days: "Days",
      expression: "Expression",
      expressionPlaceholder: "0 7 * * *",
      everyAmountPlaceholder: "30",
      timezoneOptional: "Timezone (optional)",
      timezonePlaceholder: "America/Los_Angeles",
      timezoneHelp: "Pick a common timezone or enter any valid IANA timezone.",
      jitterHelp: "Need jitter? Use Advanced → Stagger window / Stagger unit.",
      execution: "Execution",
      executionSub: "Choose when to wake, and what this job should do.",
      session: "Session",
      main: "Main",
      isolated: "Isolated",
      sessionHelp: "Main posts a system event. Isolated runs a dedicated agent turn.",
      wakeMode: "Wake mode",
      now: "Now",
      nextHeartbeat: "Next heartbeat",
      wakeModeHelp: "Now triggers immediately. Next heartbeat waits for the next cycle.",
      payloadKind: "What should run?",
      systemEvent: "Post message to main timeline",
      agentTurn: "Run assistant task (isolated)",
      systemEventHelp:
        "Sends your text to the gateway main timeline (good for reminders/triggers).",
      agentTurnHelp: "Starts an assistant run in its own session using your prompt.",
      timeoutSeconds: "Timeout (seconds)",
      timeoutPlaceholder: "Optional, e.g. 90",
      timeoutHelp:
        "Optional. Leave blank to use the gateway default timeout behavior for this run.",
      mainTimelineMessage: "Main timeline message",
      assistantTaskPrompt: "Assistant task prompt",
      deliverySection: "Delivery",
      deliverySub: "Choose where run summaries are sent.",
      resultDelivery: "Result delivery",
      announceDefault: "Announce summary (default)",
      webhookPost: "Webhook POST",
      noneInternal: "None (internal)",
      deliveryHelp: "Announce posts a summary to chat. None keeps execution internal.",
      webhookUrl: "Webhook URL",
      channel: "Channel",
      webhookPlaceholder: "https://example.com/cron",
      channelHelp: "Choose which connected channel receives the summary.",
      webhookHelp: "Send run summaries to a webhook endpoint.",
      to: "To",
      toPlaceholder: "+1555... or chat id",
      toHelp: "Optional recipient override (chat id, phone, or user id).",
      advanced: "Advanced",
      advancedHelp:
        "Optional overrides for delivery guarantees, schedule jitter, and model controls.",
      deleteAfterRun: "Delete after run",
      deleteAfterRunHelp: "Best for one-shot reminders that should auto-clean up.",
      clearAgentOverride: "Clear agent override",
      clearAgentHelp: "Force this job to use the gateway default assistant.",
      exactTiming: "Exact timing (no stagger)",
      exactTimingHelp: "Run on exact cron boundaries with no spread.",
      staggerWindow: "Stagger window",
      staggerUnit: "Stagger unit",
      staggerPlaceholder: "30",
      seconds: "Seconds",
      model: "Model",
      modelPlaceholder: "openai/gpt-5.2",
      modelHelp: "Start typing to pick a known model, or enter a custom one.",
      thinking: "Thinking",
      thinkingPlaceholder: "low",
      thinkingHelp: "Use a suggested level or enter a provider-specific value.",
      bestEffortDelivery: "Best effort delivery",
      bestEffortHelp: "Do not fail the job if delivery itself fails.",
      cantAddYet: "Can't add job yet",
      fillRequired: "Fill the required fields below to enable submit.",
      fixFields: "Fix {count} field to continue.",
      fixFieldsPlural: "Fix {count} fields to continue.",
      saving: "Saving...",
      saveChanges: "Save changes",
      addJob: "Add job",
      cancel: "Cancel",
    },
    jobList: {
      allJobs: "all jobs",
      selectJob: "(select a job)",
      enabled: "enabled",
      disabled: "disabled",
      edit: "Edit",
      clone: "Clone",
      disable: "Disable",
      enable: "Enable",
      run: "Run",
      history: "History",
      remove: "Remove",
    },
    jobDetail: {
      system: "System",
      prompt: "Prompt",
      delivery: "Delivery",
      agent: "Agent",
    },
    jobState: {
      status: "Status",
      next: "Next",
      last: "Last",
    },
    runEntry: {
      noSummary: "No summary.",
      runAt: "Run at",
      openRunChat: "Open run chat",
      next: "Next {rel}",
      due: "Due {rel}",
    },
    errors: {
      nameRequired: "Name is required.",
      scheduleAtInvalid: "Enter a valid date/time.",
      everyAmountInvalid: "Interval must be greater than 0.",
      cronExprRequired: "Cron expression is required.",
      staggerAmountInvalid: "Stagger must be greater than 0.",
      systemTextRequired: "System text is required.",
      agentMessageRequired: "Agent message is required.",
      timeoutInvalid: "If set, timeout must be greater than 0 seconds.",
      webhookUrlRequired: "Webhook URL is required.",
      webhookUrlInvalid: "Webhook URL must start with http:// or https://.",
      invalidRunTime: "Invalid run time.",
      invalidIntervalAmount: "Invalid interval amount.",
      cronExprRequiredShort: "Cron expression required.",
      invalidStaggerAmount: "Invalid stagger amount.",
      systemEventTextRequired: "System event text required.",
      agentMessageRequiredShort: "Agent message required.",
      nameRequiredShort: "Name required.",
    },
  },
  gatewayPairing: {
    title: "Add Gateway Device",
    description: "Enter the 6-digit code shown on your gateway screen.",
    codePlaceholder: "000000",
    codeLabel: "6-digit pairing code",
    cancel: "Cancel",
    pair: "Pair",
    pairing: "Pairing...",
    paired: "Paired",
    success: "Gateway paired successfully!",
    pairFailed: "Pairing failed",
    close: "Close",
    addGateway: "Add Gateway",
  },
};
